<template>
  <div class="detail-section" :class="{ 'loading-size': isLoading }">
    <div class="text-center" style="margin-right: 14%;" v-if="isLoading">
      <font-awesome-icon :icon="['fa', 'circle-notch']" class="fa-spin m-auto spinner fa-6x egx-partner-color" />
    </div>
    <div v-else>
      <div class="mb-4">
        <div class="card-wrapper">
          <MeasuresList
            :groupedMeasures="groupedMeasures"
            :fulfillSyncDetails="fulfillSyncDetails"
            :projectId="projectId"
            :popupVisible="popupVisible"
            :popupType="popupType"
            :quantities="quantities"
            :measureDetails="measureDetails"
            @select-measure="selectMeasure"
            @toggle-popup="togglePopup"
            @get-measure-details="getMeasureDetails"
            @open-dialog="openDialog"
            @duplicate-measure="duplicateMeasure"
          />
        </div>
      </div>
      <step-button
        v-if="!measuresButton"
        :steps-response="stepsResponse"
        @navigate="navigate"
        @save-data="handleStepSave"
        :data-save-required="true"
      />
      <div v-else class="d-md-flex justify-content-md-end grid-container">
        <button
          v-if="measuresButton.find(button => button.button_name.toLowerCase() === 'cancel')"
          :key="'cancel-button'"
          class="btn btn-egx"
          @click="handleButtonClick('cancel')"
          :disabled="saving"
        >
          {{ measuresButton.find(button => button.button_name.toLowerCase() === 'cancel').label }}
        </button>
        <button
          v-for="button in measuresButton.filter(button => button.button_name.toLowerCase() !== 'cancel')"
          :key="button.button_name"
          class="btn btn-egx"
          @click="handleButtonClick(button.button_name)"
          :disabled="loadingButton === button.button_name || saving"
        >
          <span v-if="loadingButton === button.button_name" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          {{ loadingButton === button.button_name ? 'Processing...' : button.label }}
        </button>
      </div>
    </div>
    <measure-apply-modal
      :project-id="projectId"
      :selected-measure="selectedMeasure"
      :selected-retrofit="selectedRetrofit"
      :fulfillSyncDetails="fulfillSyncDetails"
      :is-duplicate="isDuplicate"
      @on-hide="resetSelectedMeasures"
      @on-save="updateMeasureList"
    />
    <ConfirmationModal
      :dialogVisible="dialogVisible"
      @close="closeDialog"
      @save="deleteMeasure(pmIdsToDelete)"
      :message="fulfillSyncDetails.messages?.delete_measure_confirmation || 'Are you sure you want to delete this measure?'"
      :isDeleting="isDeleting"
    />
  </div>
</template>

<script>
import MeasureApplyModal from '@/components/project/MeasureApplyModal.vue';
import StepButton from '@/components/project/stepButton.vue';
import AccordionTabList from '@/components/project/AccordionTabList.vue';
import PopupBubble from '@/components/project/PopupBubble.vue';
import ConfirmationModal from './ConfirmationModal.vue';
import MeasuresList from './MeasuresList.vue';
import projectMixins from '@/shared/mixins/projectMixins';

export default {
  components: {
    AccordionTabList,
    StepButton,
    MeasureApplyModal,
    PopupBubble,
    ConfirmationModal,
    MeasuresList
  },

  props: {
    projectId: {
      type: String,
      required: true,
    },
    stepsResponse: Object,
    navigate: Function,
    measuresSize: Number,
    measuresButton: Array,
  },
  mixins: [projectMixins],

  data() {
    return {
      disableCard: false,
      isLoading: false,
      measures: [],
      selectedMeasure: null,
      selectedRetrofit: null,
      groupedMeasures: [],
      popupVisible: {},
      popupType: {},
      dialogVisible: false,
      isDuplicate: false,
      pmIdsToDelete: [],
      quantities: {},
      isDeleting: false,
      measuresQuantity: 0,
      measureDetails: null,
      saving: false,
      loadingButton: null,
    };
  },

  created() {
    this.isLoading = true;
    this.$store.dispatch('fulfill_integrate/get_project_measures', { project_id: this.projectId })
      .then((response) => {
        this.groupedMeasures = this.sortedList(response);
        this.groupedMeasures.forEach(group => {
          group.retrofits.forEach(retrofit => {
            this.$set(this.quantities, retrofit.pm_id, retrofit.quantity);
          });
        });
        this.measuresQuantity = this.groupedMeasures.length;
      })
      .catch((error) => {
        console.error('Error fetching project measures:', error);
      }).finally(() => {
        this.$store.dispatch('fulfill_integrate/get_measures', { project_id: this.projectId, project_status: this.projectStatus })
          .then((response) => {
            this.measures = response;
            this.groupedMeasures.forEach(group => {
              group.retrofits.forEach(retrofit => {
                retrofit['hierarchy_level_1'] = this.getSpecificMeasure(retrofit.id)?.hierarchy_level_1;
                retrofit['program_specific_measure_details'] = this.getSpecificMeasure(retrofit.id)?.program_specific_measure_details;
              });
            });
            this.isLoading = false;
          }).catch((response) => {
            console.error('Error fetching measures:', response);
            this.isLoading = false;
          });
      })
  },

  computed: {
    settings() {
      return this.$store.state.partners.currentPartner.settings;
    },

    fulfillSyncDetails() {
      return this.settings.fulfill_sync_details;
    },
    currentProject() {
      return this.$store.state.fulfill_integrate.project
    },
  },

  methods: {
    sortedList(list) {
      if (!list) return [];
      return list.sort((a, b) => a.sorting_tag - b.sorting_tag);
    },

    nextStep(step) {
      this.checkLockedProject();

      if (step === 'nextStep') {
        this.navigate();
      } else if (step === 'linkStep') {
        this.redirectPath(this.stepsResponse.add_btn_url);
      }
    },

    selectMeasure(measure) {
      this.checkLockedProject();

      this.selectedMeasure = measure;
      this.selectedRetrofit = measure;
      this.isDuplicate = false;
      this.$nextTick(() => {
        this.$bvModal.show('measure-apply');
      });
    },

    duplicateMeasure(measure) {
      this.checkLockedProject();
      this.selectedMeasure = measure;
      this.selectedRetrofit = measure;
      this.isDuplicate = true;
      this.$nextTick(() => {
        this.$bvModal.show('measure-apply');
      });
    },

    resetSelectedMeasures() {
      this.selectedMeasure = null;
    },

    updateMeasureList() {
      this.isLoading = true;
      this.$store.dispatch('fulfill_integrate/get_project_measures', { project_id: this.projectId})
        .then((response) => {
          this.groupedMeasures = this.sortedList(response);
          if (this.groupedMeasures.length > 0) {
            this.measuresQuantity = this.groupedMeasures[0].retrofits.length;
          } else {
            this.measuresQuantity = 0;
          }
        }).catch((error) => {
          this.$notify({
            group: 'notice',
            text: error?.message,
          });
        }).finally(() =>{
          this.isLoading = false;
        });
    },
    togglePopup(retrofitId, type = 'installationDetails') {
      this.$set(this.popupVisible, retrofitId, !this.popupVisible[retrofitId]);
      this.$set(this.popupType, retrofitId, type);
    },
    getMeasureDetails(retrofitId) {
      for (const group of this.measures) {
        const measure = group.retrofits.find((retrofit) => retrofit.id === retrofitId);
        if (measure) {
          this.measureDetails = measure.program_specific_measure_details;
          break;
        }
      }
    },

    getSpecificMeasure(retrofitId) {
      for (const group of this.measures) {
        const measure = group.retrofits.find((retrofit) => retrofit.id === retrofitId);
        if (measure) { return measure; }
      }
    },

    openDialog(retrofit) {
      if(retrofit) {
        this.pmIdsToDelete.push(retrofit.pm_id);
      }
      this.dialogVisible = true;
    },

    closeDialog() {
      this.pmIdsToDelete = [];
      this.dialogVisible = false;
    },

    deleteMeasure(pmIds = [], step) {
      this.checkLockedProject();
      this.isDeleting = true;
      this.isLoading = true;
      const payload = {
        pm_ids: pmIds.length ? pmIds : [this.selectedRetrofit.pm_id],
      };
      this.$store.dispatch('fulfill_integrate/delete_measures', { project_id: this.projectId, ...payload })
        .then(async () => {
          this.dialogVisible = false;
          this.isDeleting = false;
          this.updateMeasureList();
          await this.$store.dispatch('fulfill_integrate/get_project_selected', { project_id: this.projectId });
          if (step) {
            this.nextStep(step);
          }
        });
    },

    handleStepSave(step) {
      const pmIdsToDelete = [];
      const updatedRetrofits = [];
      this.checkLockedProject();
      this.groupedMeasures.forEach(group => {
        group.retrofits.forEach(retrofit => {
          if (!retrofit.quantity || retrofit.quantity <= 0) {
            pmIdsToDelete.push(retrofit.pm_id);
          } else if (this.quantities[retrofit.pm_id] && retrofit.quantity !== this.quantities[retrofit.pm_id]) {
            updatedRetrofits.push(retrofit);
          }
        });
      });

      if (updatedRetrofits.length) {
        this.saveUpdatedRetrofits(updatedRetrofits, step);
      }

      if (pmIdsToDelete.length) {
        this.deleteMeasure(pmIdsToDelete, step);
      } else if (!updatedRetrofits.length) {
        this.loadingButton = null;
        this.nextStep(step);
      }
    },

    saveUpdatedRetrofits(retrofits, step) {
      this.checkLockedProject();
      this.saving = true;
      const data = {
        details: {
          isFieldUpdate: true,
          project_id: this.projectId,
          retrofit: retrofits,
        }
      };

      this.$store.dispatch('fulfill_integrate/post', data)
        .then(response => {
          retrofits.forEach(retrofit => {
            this.$set(this.quantities, retrofit.pm_id, retrofit.quantity);
          });
          this.$notify({
            group: 'notice',
            text: `Measure(s) updated successfully.`,
          });
          this.saving = false;
          this.loadingButton = null;
          this.nextStep(step);
        })
        .catch(error => {
          console.error('Error updating Measure(s):', error.message);
          this.$notify({
            group: 'notice-stay',
            type: 'error',
            text: `Failed to update Measure(s).`,
          });
          this.saving = false;
          this.loadingButton = null;
        });
    },

    handleButtonClick(buttonName) {
      if (this.locked(this.currentProject)) {
        this.$router.push('/applications');
        return;
      }
      this.loadingButton = buttonName;
      if (buttonName.toLowerCase() === 'savecontinue' || buttonName.toLowerCase() === 'savechanges') {
        this.handleStepSave(buttonName === 'savecontinue' ? 'nextStep' : null);
      } else if (buttonName.toLowerCase() === 'cancel') {
        this.navigate();
        this.loadingButton = null;
      }
    },
    checkLockedProject() {
      this.$store.dispatch('fulfill_integrate/get_project_selected', { project_id: this.currentProject.id })
      .then((response) => {
        if(this.locked(response.project)) {
          this.$router.push('/applications');
          return;
        }
        return false;
      })
      .catch((error) => {
        console.log(error);
        this.$router.push({ name: 'Offers' });
      });
    },
  }
}
</script>

<style lang="scss" scoped>

@import '../../assets/stylesheets/components/project/measures.scss';
@import '../../assets/stylesheets/components/project/contact.scss';

.retrofit-card {
  min-height: 8rem;
  justify-content: center;
}

.overflow-visible {
  overflow: visible !important;
}
.grid-container {
  display: grid;
  grid-auto-rows: auto;
  transform: rotate(180deg);
  direction: rtl;
  column-gap: 20px;
}

.grid-container > * {
  transform: rotate(180deg);
  margin-right: 0;
  margin-bottom: 15px;
}
</style>
